import { Link as RouterLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { docIcon } from '../recommendedDocs/docIcons';
import type { RecommendedDocEntry } from '../recommendedDocs/recommendedDocsApi/types';

/*
 * Company and Team recommended documentation. Each entry is already its own
 * card — an admin-curated link into a specific doc page, with its own title,
 * icon, and description — so this doesn't wrap them in a second, boxed
 * container the way DocListPanel wraps its plain text rows; that would just
 * double up the chrome. Instead the two scopes sit directly on the page
 * background as plain headed sections, split by a hairline.
 */
const useStyles = makeStyles({
  panel: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  divider: {
    height: 1,
    margin: 0,
    border: 'none',
    background: 'var(--hone-portal-hairline-soft)',
  },
  header: {
    margin: '0 0 12px',
    fontSize: 13.5,
    fontWeight: 600,
    color: 'var(--bui-fg-primary)',
  },
  // Wraps rather than stretching into a fixed column count, so an odd card
  // count doesn't leave a stretched, empty cell in the last row.
  grid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    flex: '0 1 260px',
    minWidth: 0,
    padding: 16,
    borderRadius: 10,
    background: 'var(--bui-bg-neutral-2)',
    boxShadow: 'inset 0 0 0 1px var(--bui-border-1)',
    textDecoration: 'none',
    '&:hover': {
      background: 'var(--bui-bg-neutral-3)',
    },
    '&:focus-visible': {
      outline: 'none',
      boxShadow: '0 0 0 2px #039cfb',
    },
  },
  cardTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
  },
  cardIcon: {
    width: 16,
    height: 16,
    flexShrink: 0,
    opacity: 0.6,
  },
  cardTitle: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: 13.5,
    fontWeight: 600,
    color: 'var(--bui-fg-primary)',
  },
  cardDescription: {
    margin: 0,
    fontSize: 12.5,
    lineHeight: 1.5,
    color: 'var(--bui-fg-secondary)',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  empty: {
    margin: 0,
    fontSize: 12.5,
    color: 'var(--hone-portal-fg-tertiary)',
  },
});

type Classes = ReturnType<typeof useStyles>;

const RecommendedDocsCards = ({
  entries,
  emptyLabel,
  classes,
}: {
  entries: RecommendedDocEntry[];
  emptyLabel: string;
  classes: Classes;
}) => {
  if (entries.length === 0) {
    return <p className={classes.empty}>{emptyLabel}</p>;
  }
  return (
    <div className={classes.grid}>
      {entries.map(entry => {
        const Icon = docIcon(entry.icon);
        return (
          <RouterLink key={entry.path} to={entry.path} className={classes.card}>
            <span className={classes.cardTitleRow}>
              <Icon className={classes.cardIcon} />
              <span className={classes.cardTitle}>{entry.title}</span>
            </span>
            <p className={classes.cardDescription}>{entry.description}</p>
          </RouterLink>
        );
      })}
    </div>
  );
};

export type RecommendedDocsSectionProps = {
  company: RecommendedDocEntry[];
  team: RecommendedDocEntry[];
  companyEmptyLabel: string;
  teamEmptyLabel: string;
};

export const RecommendedDocsSection = ({
  company,
  team,
  companyEmptyLabel,
  teamEmptyLabel,
}: RecommendedDocsSectionProps) => {
  const classes = useStyles();
  return (
    <div className={classes.panel}>
      <section>
        <h3 className={classes.header}>Company recommended</h3>
        <RecommendedDocsCards entries={company} emptyLabel={companyEmptyLabel} classes={classes} />
      </section>
      <hr className={classes.divider} />
      <section>
        <h3 className={classes.header}>Team recommended</h3>
        <RecommendedDocsCards entries={team} emptyLabel={teamEmptyLabel} classes={classes} />
      </section>
    </div>
  );
};
